import {
  signInWithPopup,
  signInWithRedirect,
  signOut as fbSignOut,
  User as FirebaseUser,
  Unsubscribe,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { auth, googleProvider, db } from "./firebase";
import {
  UserProfile,
  UserRole,
  UserStatus,
  NotificationPreference,
} from "../types";

export const ALLOWED_DOMAIN = "mlconnections.com";

/**
 * Validates if an email address belongs to the allowed MLC Google Workspace domain.
 */
export function isAllowedDomain(email: string | null | undefined): boolean {
  if (!email) return false;
  return email.toLowerCase().endsWith(`@${ALLOWED_DOMAIN}`);
}

/**
 * Initiates Google SSO (popup by default, or redirect) and returns the synced UserProfile.
 */
export async function signInWithGoogle(
  preferRedirect = false,
): Promise<UserProfile> {
  if (preferRedirect) {
    await signInWithRedirect(auth, googleProvider);
    return new Promise<UserProfile>(() => {});
  }

  let fbUser: FirebaseUser;
  try {
    const result = await signInWithPopup(auth, googleProvider);
    fbUser = result.user;
  } catch (err: unknown) {
    const authErr = err as { code?: string; message?: string };
    if (
      authErr?.code === "auth/popup-closed-by-user" ||
      authErr?.code === "auth/cancelled-popup-request"
    ) {
      throw new Error(
        "Sign-in cancelled. Please click the button to try again.",
      );
    }
    console.warn("signInWithPopup failed, falling back to redirect:", err);
    await signInWithRedirect(auth, googleProvider);
    return new Promise<UserProfile>(() => {});
  }

  if (!isAllowedDomain(fbUser.email)) {
    await fbSignOut(auth);
    throw new Error(
      `Access restricted. Only @${ALLOWED_DOMAIN} Google Workspace accounts are permitted.`,
    );
  }

  return syncUserProfile(fbUser);
}

/**
 * Signs out the current authenticated user.
 */
export async function signOut(): Promise<void> {
  await fbSignOut(auth);
}

/**
 * Ensures a UserProfile document exists in mlc-bug-tracking-db.
 * If new, initializes with status = 'pending_approval' and role = 'end_user'.
 */
export async function syncUserProfile(
  fbUser: FirebaseUser,
): Promise<UserProfile> {
  if (!fbUser.email || !isAllowedDomain(fbUser.email)) {
    throw new Error(`Invalid domain for user: ${fbUser.email}`);
  }

  const userDocRef = doc(db, "users", fbUser.uid);
  const snap = await getDoc(userDocRef);

  if (!snap.exists()) {
    // Check if user was pre-staged by HR directory sync
    let stagedData: Partial<UserProfile> = {};
    try {
      const q = query(
        collection(db, "users"),
        where("email", "==", fbUser.email.toLowerCase()),
      );
      const emailSnap = await getDocs(q);
      const firstDoc = emailSnap.docs[0];
      if (firstDoc) {
        stagedData = firstDoc.data() as Partial<UserProfile>;
      }
    } catch {
      // In case rules or offline cache prevent query, proceed with defaults
    }

    const emailFallback = fbUser.email.split("@")[0] || "User";
    const newProfile: UserProfile = {
      uid: fbUser.uid,
      email: fbUser.email.toLowerCase(),
      displayName:
        fbUser.displayName || stagedData.displayName || emailFallback,
      photoURL: fbUser.photoURL || stagedData.photoURL || undefined,
      department: stagedData.department,
      hrEmployeeId: stagedData.hrEmployeeId,
      role: "end_user",
      status: "pending_approval",
      allowedApps: [],
      approvedBy: null,
      approvedAt: null,
      createdAt: stagedData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    await setDoc(userDocRef, {
      ...newProfile,
      serverCreatedAt: serverTimestamp(),
      serverUpdatedAt: serverTimestamp(),
    });

    return newProfile;
  }

  const existing = snap.data() as UserProfile;

  // Sync basic display info if changed
  const updates: Partial<UserProfile> = {};
  if (fbUser.displayName && fbUser.displayName !== existing.displayName) {
    updates.displayName = fbUser.displayName;
  }
  if (fbUser.photoURL && fbUser.photoURL !== existing.photoURL) {
    updates.photoURL = fbUser.photoURL;
  }

  if (Object.keys(updates).length > 0) {
    updates.updatedAt = new Date().toISOString();
    await updateDoc(userDocRef, {
      ...updates,
      serverUpdatedAt: serverTimestamp(),
    });
    return { ...existing, ...updates };
  }

  return existing;
}

/**
 * Subscribes to real-time updates for a user's profile document.
 */
export function subscribeToUserProfile(
  uid: string,
  onUpdate: (profile: UserProfile | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const userDocRef = doc(db, "users", uid);
  return onSnapshot(
    userDocRef,
    (snap) => {
      if (snap.exists()) {
        onUpdate(snap.data() as UserProfile);
      } else {
        onUpdate(null);
      }
    },
    (err) => {
      if (onError) onError(err);
    },
  );
}

/**
 * IT Admin helper: Updates a user's role, status, and allowed applications.
 */
export async function updateUserGovernance(
  targetUid: string,
  updates: {
    role?: UserRole;
    status?: UserStatus;
    allowedApps?: string[];
    approvedBy?: string;
    notificationPreference?: NotificationPreference;
  },
): Promise<void> {
  const userDocRef = doc(db, "users", targetUid);
  const payload: Record<string, unknown> = {
    ...updates,
    updatedAt: new Date().toISOString(),
    serverUpdatedAt: serverTimestamp(),
  };

  if (updates.status === "active" && updates.approvedBy) {
    payload.approvedAt = new Date().toISOString();
  }

  await updateDoc(userDocRef, payload);
}

/**
 * IT Admin helper: Deletes a user document from mlc-bug-tracking-db.
 * Allowed by security rules for IT Administrators.
 */
export async function deleteUserRecord(targetUid: string): Promise<void> {
  const userDocRef = doc(db, "users", targetUid);
  await deleteDoc(userDocRef);
}

/**
 * Fetches all registered and pre-staged user profiles from mlc-bug-tracking-db.
 */
export async function fetchUsers(): Promise<UserProfile[]> {
  const usersRef = collection(db, "users");
  const snap = await getDocs(usersRef);
  return snap.docs.map((docSnap) => docSnap.data() as UserProfile);
}

/**
 * Subscribes to the entire users directory for real-time governance synchronization.
 */
export function subscribeToUsers(
  onUpdate: (users: UserProfile[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const usersRef = collection(db, "users");
  return onSnapshot(
    usersRef,
    (snap) => {
      const users = snap.docs.map((docSnap) => docSnap.data() as UserProfile);
      onUpdate(users);
    },
    (err) => {
      if (onError) onError(err);
    },
  );
}

export interface HrSyncResponse {
  success: boolean;
  message: string;
  syncedCount: number;
  stagedCount: number;
  updatedCount: number;
  timestamp: string;
}

export interface MinimalHrEmployee {
  id: string;
  email: string;
  workEmail?: string;
  displayName: string;
  department: string;
  title: string;
  photoURL?: string | null;
  status: string;
  recordNumber?: string;
}

export interface HrPreviewResponse {
  success: boolean;
  totalActive: number;
  activeStaff: MinimalHrEmployee[];
  timestamp: string;
}

export interface HrImportResponse {
  success: boolean;
  importedCount: number;
  updatedCount: number;
  totalActiveCount: number;
  importedUsers: Array<{ uid: string; email: string; displayName: string }>;
  timestamp: string;
}

export const DEFAULT_HR_SYNC_URL =
  "https://bt-syncfromhr-qbuhtyitjq-uc.a.run.app";

/**
 * Builds JSON headers carrying the signed-in user's Firebase ID token.
 * The backend verifies the token and the caller's it_admin role.
 */
async function buildAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("You must be signed in to perform this action.");
  }
  const token = await user.getIdToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Lists active employees from the HR directory preview via bt_syncFromHr.
 * Inactive and terminated staff are strictly excluded by the backend.
 */
export async function listActiveHrStaff(
  _callerEmail?: string,
  syncUrl: string = DEFAULT_HR_SYNC_URL,
): Promise<MinimalHrEmployee[]> {
  const headers = await buildAuthHeaders();

  const response = await fetch(syncUrl, {
    method: "POST",
    headers,
    body: JSON.stringify({ action: "preview" }),
  });

  if (!response.ok) {
    let errorMessage = `Failed to list HR directory (${response.status})`;
    try {
      const errorJson = (await response.json()) as {
        details?: string;
        error?: string;
      };
      errorMessage = errorJson.details || errorJson.error || errorMessage;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  const data = (await response.json()) as HrPreviewResponse;
  return data.activeStaff || [];
}

/**
 * Selectively imports specified active staff from HR into mlc-bug-tracking-db.
 */
export async function importSelectedHrStaff(
  selectedUserIds: string[],
  defaultRole: UserRole = "end_user",
  defaultStatus: UserStatus = "active",
  _callerEmail?: string,
  syncUrl: string = DEFAULT_HR_SYNC_URL,
): Promise<HrImportResponse> {
  const headers = await buildAuthHeaders();

  const response = await fetch(syncUrl, {
    method: "POST",
    headers,
    body: JSON.stringify({
      action: "import",
      selectedUserIds,
      defaultRole,
      defaultStatus,
    }),
  });

  if (!response.ok) {
    let errorMessage = `Failed to import selected HR staff (${response.status})`;
    try {
      const errorJson = (await response.json()) as {
        details?: string;
        error?: string;
      };
      errorMessage = errorJson.details || errorJson.error || errorMessage;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return (await response.json()) as HrImportResponse;
}

/**
 * Triggers on-demand HR directory synchronization via bt_syncFromHr Cloud Function.
 */
export async function triggerHrSync(
  _callerEmail?: string,
  syncUrl: string = DEFAULT_HR_SYNC_URL,
): Promise<HrSyncResponse> {
  const headers = await buildAuthHeaders();

  const response = await fetch(syncUrl, {
    method: "POST",
    headers,
  });

  if (!response.ok) {
    let errorMessage = `HR sync failed (${response.status})`;
    try {
      const errorJson = (await response.json()) as {
        error?: string;
        details?: string;
      };
      if (errorJson.details || errorJson.error) {
        errorMessage = errorJson.details || errorJson.error || errorMessage;
      }
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return (await response.json()) as HrSyncResponse;
}

/**
 * Updates an authenticated user's email notification cadence preference in mlc-bug-tracking-db.
 */
export async function updateNotificationPreference(
  uid: string,
  preference: NotificationPreference,
): Promise<void> {
  const userDocRef = doc(db, "users", uid);
  const nowIso = new Date().toISOString();
  await updateDoc(userDocRef, {
    notificationPreference: preference,
    updatedAt: nowIso,
    serverUpdatedAt: serverTimestamp(),
  });
}
